import { useEffect, useState, useRef, memo } from 'react';
import { Box, FormControl, Select, MenuItem, IconButton, Popover, Button, Tooltip, Typography, OutlinedInput } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import useApi from 'hooks/useApi';
import { useParams } from 'react-router';
import { useField } from 'formik';

type ReportItem = {
  _id: string;
  name: string;
  uid: string;
};

/**
 * What gets persisted on the field. `enabled` is owned by the caller's checkbox —
 * this component only ever edits uid/name and spreads the rest through untouched.
 */
type SelectedReport = { enabled?: boolean; uid: string | null; name: string | null } | null;

type ReportformProps = {
  /** Formik path to write the selected report into, e.g. `fields.0.settings.report` */
  name: string;
  label?: string;
  onReportsLoaded?: (list: ReportItem[]) => void;
};

const Reportform = ({ name, label = 'Select Report', onReportsLoaded }: ReportformProps) => {
  const { teamUid } = useParams();
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [newReport, setNewReport] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { getNodeReports, createNodeReport } = useApi();

  const [field, , helpers] = useField<SelectedReport>(name);
  const { value: selected } = field;
  const { setValue } = helpers;
  const selectedUid = selected?.uid || '';

  const toList = (res: any): ReportItem[] =>
    Array.isArray(res?.data)
      ? res.data
          .map((item: any) => ({ _id: item._id, name: item.name, uid: item.uid }))
          .sort((a: ReportItem, b: ReportItem) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      : [];

  useEffect(() => {
    if (!teamUid) return;

    getNodeReports(teamUid)
      .then((res: any) => {
        const list = toList(res);
        setReports(list);
        onReportsLoaded?.(list);
      })
      .catch(() => setReports([]));
  }, [teamUid]);

  const handleAddReport = async () => {
    const trimmed = newReport.trim();
    if (!trimmed) return;
    if (reports.some((r) => r.name === trimmed)) {
      setError('Report already exists');
      return;
    }
    if (!teamUid) {
      setError('Team UID missing');
      return;
    }

    try {
      const created = await createNodeReport({ name: trimmed }, teamUid);
      const refreshed = toList(await getNodeReports(teamUid));
      setReports(refreshed);
      onReportsLoaded?.(refreshed);
      // responseHandler wraps payloads as { success, message, data, statusCode }
      const createdUid = created?.data?.uid || created?.uid;
      if (createdUid) {
        setValue({ ...(selected || {}), uid: createdUid, name: trimmed });
      }
      setAnchorEl(null);
      setNewReport('');
      setError('');
    } catch {
      setError('Failed to add report');
    }
  };

  return (
    <Box my={1}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>

      <Box display="flex" alignItems="center" gap={1}>
        <FormControl fullWidth size="small">
          <Select
            value={reports.some((r) => r.uid === selectedUid) ? selectedUid : ''}
            onChange={(e: any) => {
              const uid = e.target.value;
              const picked = reports.find((r) => r.uid === uid);
              setValue({
                ...(selected || {}),
                uid: picked ? picked.uid : null,
                name: picked ? picked.name : null
              });
            }}
            displayEmpty
            renderValue={(uid) => {
              if (!uid) {
                // List not loaded yet, or the report was deleted — fall back to
                // the name captured when it was picked.
                return selected?.name ? selected.name : <span style={{ color: '#aaa' }}>Choose report...</span>;
              }
              const report = reports.find((r) => r.uid === uid);
              return report ? report.name : selected?.name || uid;
            }}
          >
            {reports.map((r) => (
              <MenuItem key={r.uid} value={r.uid}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Add new report">
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ flexShrink: 0 }}>
            <AddCircleOutlineIcon sx={{ color: 'gray' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={2} width={250}>
          <Typography>Add new report</Typography>
          <OutlinedInput
            fullWidth
            value={newReport}
            onChange={(e) => {
              setNewReport(e.target.value);
              setError('');
            }}
            inputRef={inputRef}
            onKeyDown={(e) => e.key === 'Enter' && handleAddReport()}
            error={Boolean(error)}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
          <Box mt={1} textAlign="right">
            <Button variant="contained" onClick={handleAddReport} disabled={!newReport.trim()}>
              Add
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default memo(Reportform);
